import React, { useState, useEffect, useMemo } from 'react';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { ArrowUpIcon, ArrowDownIcon, ChevronDownIcon } from '@heroicons/react/20/solid';
import { Spinner } from '../../components/shared/Spinner';
import CategoryDetail from './CategoryDetail';
import { motion } from 'framer-motion';
import { useData } from '../../contexts/DataContext';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Color palette for charts
const COLORS = {
  primary: '#3b82f6',      // Blue
  secondary: '#8b5cf6',    // Violet
  success: '#10b981',      // Emerald
  danger: '#f43f5e',       // Rose
  warning: '#f59e0b',      // Amber
  info: '#3b82f6',         // Blue
  neutral: '#94a3b8',      // Slate
  textDark: '#1e293b',     // Slate 800
  textMedium: '#475569',   // Slate 600
  textLight: '#94a3b8',    // Slate 400
  bgLight: '#f8fafc',      // Slate 50
  chartColors: [
    '#3b82f6', // Blue
    '#10b981', // Emerald
    '#f43f5e', // Rose
    '#8b5cf6', // Violet
    '#f59e0b', // Amber
    '#ec4899', // Pink
    '#06b6d4', // Cyan
  ],
  chartPositive: '#10b981',  // Emerald
  chartNegative: '#f43f5e',  // Rose
  chartNeutral: '#94a3b8',   // Slate
};

// Default chart options with shared configuration
const defaultChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'bottom',
      labels: {
        usePointStyle: true,
        padding: 20,
        font: {
          family: "'Inter', sans-serif",
          size: 12
        }
      }
    },
    tooltip: {
      backgroundColor: 'rgba(30, 41, 59, 0.9)',
      titleFont: {
        family: "'Inter', sans-serif",
        size: 14,
        weight: 'bold'
      },
      bodyFont: {
        family: "'Inter', sans-serif",
        size: 13
      },
      padding: 12,
      cornerRadius: 8,
      displayColors: true,
      usePointStyle: true,
      boxPadding: 6
    }
  }
};

// Individual Category Card component
const CategoryCard = ({ category, onClick, isActive }) => {
  // Calculate sentiment distribution for visualization
  const sentimentData = [
    { name: 'Positive', value: category.positivePercentage, color: 'bg-emerald-500' },
    { name: 'Neutral', value: category.neutralPercentage, color: 'bg-slate-300' },
    { name: 'Negative', value: category.negativePercentage, color: 'bg-rose-500' }
  ];

  // Safely handle category name transformation
  const categoryName = category.name ? category.name.replace(/_/g, ' ').toUpperCase() : 'CATEGORY';

  return (
    <div 
      className={`
        bg-white rounded-lg p-4 cursor-pointer transition-all
        ${isActive 
          ? 'ring-2 ring-blue-500 shadow-md' 
          : 'hover:shadow-md border border-slate-200'
        }
      `}
      onClick={() => onClick(category)}
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-medium text-slate-800">{categoryName}</h3>
        <span className="bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded-full">
          {category.total} mentions
        </span>
      </div>
      
      {/* Sentiment distribution bar */}
      <div className="h-2 w-full bg-slate-100 rounded-full mb-3 overflow-hidden">
        <div className="flex h-full">
          {sentimentData.map((item, index) => (
            <div 
              key={index}
              className={item.color}
              style={{ width: `${item.value}%` }}
            />
          ))}
        </div>
      </div>
      
      <div className="grid grid-cols-3 gap-2 text-xs">
        <div className="flex flex-col items-center p-2 bg-slate-50 rounded">
          <span className="text-slate-500">Positive</span>
          <span className="font-medium text-emerald-600">{category.positivePercentage}%</span>
        </div>
        <div className="flex flex-col items-center p-2 bg-slate-50 rounded">
          <span className="text-slate-500">Neutral</span>
          <span className="font-medium text-slate-600">{category.neutralPercentage}%</span>
        </div>
        <div className="flex flex-col items-center p-2 bg-slate-50 rounded">
          <span className="text-slate-500">Negative</span>
          <span className="font-medium text-rose-600">{category.negativePercentage}%</span>
        </div>
      </div>
    </div>
  );
};

// Main Category Analytics Component
const CategoryAnalytics = () => {
  // Get data from context
  const { data: bankData } = useData();

  // All hooks must be at the top
  const [categoryComparison, setCategoryComparison] = useState([]);
  const [data, setData] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [sortBy, setSortBy] = useState('mentions');
  const [sortDirection, setSortDirection] = useState('desc');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!bankData || !bankData.data) return;
    
    setLoading(true);
    
    // Process the data to create category comparison
    const categoryStats = {};
    
    bankData.data.forEach(item => {
      if (item.processed_data && item.processed_data.categories) {
        item.processed_data.categories.forEach(cat => {
          if (!categoryStats[cat.category]) {
            categoryStats[cat.category] = {
              positive: 0,
              negative: 0,
              neutral: 0,
              total: 0
            };
          }
          
          categoryStats[cat.category][cat.sentiment]++;
          categoryStats[cat.category].total++;
        });
      }
    });

    // Convert to comparison format
    const comparison = Object.entries(categoryStats).map(([category, stats]) => ({
      category: category.replace(/_/g, ' '),
      positive: stats.positive,
      negative: stats.negative, 
      neutral: stats.neutral,
      total: stats.total,
      positivePercentage: Math.round((stats.positive / stats.total) * 100),
      negativePercentage: Math.round((stats.negative / stats.total) * 100),
      neutralPercentage: Math.round((stats.neutral / stats.total) * 100)
    })).sort((a, b) => b.total - a.total);

    setCategoryComparison(comparison);
    
    // Process raw data into the format needed by this component
    const processedData = processRawData(bankData);
    setData(processedData);
    setLoading(false);
  }, [bankData]);

  // Memoized sorting and filtering
  const sortedData = useMemo(() => {
    if (!data || data.length === 0) return [];
    
    const sorted = [...data].sort((a, b) => {
      let valueA, valueB;
      
      switch (sortBy) {
        case 'mentions':
          valueA = a.total || 0;
          valueB = b.total || 0;
          break;
        case 'positive':
          valueA = a.positivePercentage || 0;
          valueB = b.positivePercentage || 0;
          break;
        case 'negative':
          valueA = a.negativePercentage || 0;
          valueB = b.negativePercentage || 0;
          break;
        case 'neutral':
          valueA = a.neutralPercentage || 0;
          valueB = b.neutralPercentage || 0;
          break;
        default:
          valueA = a.total || 0;
          valueB = b.total || 0;
      }
      
      if (sortDirection === 'asc') {
        return valueA - valueB;
      } else {
        return valueB - valueA;
      }
    });
    
    return sorted;
  }, [data, sortBy, sortDirection]);

  if (!bankData) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading data...</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Processing category analytics...</p>
        </div>
      </div>
    );
  }

  // Process the raw data from processed_bank_data.json into the format needed by this component
  const processRawData = (rawData) => {
    if (!rawData || !rawData.data || !Array.isArray(rawData.data)) {
      console.error('Invalid data format:', rawData);
      return [];
    }
    
    // Get categories from metadata if available
    const categories = rawData.metadata?.categories || [];
    
    // Create a map to store category statistics
    const categoryMap = {};
    
    // Create a map to store subcategory statistics per category
    const subcategoryMap = {};
    
    // Initialize categories from metadata
    categories.forEach(category => {
      categoryMap[category] = {
        name: category,
        total: 0,
        positive: 0,
        neutral: 0,
        negative: 0,
        positivePercentage: 0,
        neutralPercentage: 0,
        negativePercentage: 0
      };
      
      subcategoryMap[category] = {};
    });
    
    // Process each feedback item
    rawData.data.forEach(item => {
      // Skip items without processed data
      if (!item.processed_data) return;
      
      // Process each category in the feedback
      if (item.processed_data.categories) {
        item.processed_data.categories.forEach(cat => {
          const { category, sentiment } = cat;
          
          // Initialize category if it doesn't exist yet
          if (!categoryMap[category]) {
            categoryMap[category] = {
              name: category,
              total: 0,
              positive: 0,
              neutral: 0,
              negative: 0,
              positivePercentage: 0,
              neutralPercentage: 0,
              negativePercentage: 0
            };
            
            subcategoryMap[category] = {};
          }
          
          // Update category stats
          categoryMap[category].total += 1;
          
          if (sentiment === 'positive') {
            categoryMap[category].positive += 1;
          } else if (sentiment === 'neutral') {
            categoryMap[category].neutral += 1;
          } else if (sentiment === 'negative') {
            categoryMap[category].negative += 1;
          }
        });
      }
      
      // Process subcategories
      if (item.processed_data.subcategories) {
        item.processed_data.subcategories.forEach(subcat => {
          const { category, subcategory, sentiment } = subcat;
          
          // Skip if parent category doesn't exist
          if (!categoryMap[category]) return;
          
          // Initialize subcategory if it doesn't exist yet
          if (!subcategoryMap[category][subcategory]) {
            subcategoryMap[category][subcategory] = {
              subcategory: subcategory,
              name: subcategory.replace(/_/g, ' '),
              mentions: 0,
              positive: 0,
              neutral: 0,
              negative: 0,
              positivePercentage: 0,
              neutralPercentage: 0,
              negativePercentage: 0
            };
          }
          
          // Update subcategory stats
          subcategoryMap[category][subcategory].mentions += 1;
          
          if (sentiment === 'positive') {
            subcategoryMap[category][subcategory].positive += 1;
          } else if (sentiment === 'neutral') {
            subcategoryMap[category][subcategory].neutral += 1;
          } else if (sentiment === 'negative') {
            subcategoryMap[category][subcategory].negative += 1;
          }
        });
      }
    });
    
    // Calculate percentages for categories and add subcategories
    return Object.values(categoryMap).map(category => {
      if (category.total > 0) {
        category.positivePercentage = Math.round((category.positive / category.total) * 100);
        category.neutralPercentage = Math.round((category.neutral / category.total) * 100);
        category.negativePercentage = Math.round((category.negative / category.total) * 100);
        
        // Ensure percentages add up to exactly 100%
        const sum = category.positivePercentage + category.neutralPercentage + category.negativePercentage;
        if (sum !== 100) {
          // Adjust the largest percentage to make the sum 100
          if (category.positivePercentage >= category.neutralPercentage && 
              category.positivePercentage >= category.negativePercentage) {
            category.positivePercentage += (100 - sum);
          } else if (category.neutralPercentage >= category.positivePercentage && 
                    category.neutralPercentage >= category.negativePercentage) {
            category.neutralPercentage += (100 - sum);
          } else {
            category.negativePercentage += (100 - sum);
          }
        }
      }
      
      // Process subcategories for this category
      if (subcategoryMap[category.name]) {
        const subcats = Object.values(subcategoryMap[category.name]);
        
        // Calculate percentages for each subcategory
        subcats.forEach(subcat => {
          if (subcat.mentions > 0) {
            subcat.positivePercentage = Math.round((subcat.positive / subcat.mentions) * 100);
            subcat.neutralPercentage = Math.round((subcat.neutral / subcat.mentions) * 100);
            subcat.negativePercentage = Math.round((subcat.negative / subcat.mentions) * 100);
            
            // Ensure percentages add up to exactly 100%
            const sum = subcat.positivePercentage + subcat.neutralPercentage + subcat.negativePercentage;
            if (sum !== 100) {
              // Adjust the largest percentage to make the sum 100
              if (subcat.positivePercentage >= subcat.neutralPercentage && 
                  subcat.positivePercentage >= subcat.negativePercentage) {
                subcat.positivePercentage += (100 - sum);
              } else if (subcat.neutralPercentage >= subcat.positivePercentage && 
                        subcat.neutralPercentage >= subcat.negativePercentage) {
                subcat.neutralPercentage += (100 - sum);
              } else {
                subcat.negativePercentage += (100 - sum);
              }
            }
          }
        });
        
        // Add subcategories to the category object
        category.subcategories = subcats.filter(subcat => subcat.mentions > 0);
      }
      
      return category;
    }).filter(category => category.total > 0); // Only return categories with mentions
  };
  
  // Handle category selection
  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
  };
  
  // Handle sort change
  const handleSortChange = (criteriaName) => {
    if (sortBy === criteriaName) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(criteriaName);
      setSortDirection('desc');
    }
  };
  
  // Handle closing the detail view
  const handleCloseDetail = () => {
    setSelectedCategory(null);
  };
  
  // Render a sort button with appropriate icon
  const renderSortButton = (name, label) => {
    const isActive = sortBy === name;
    
    return (
      <button
        className={`flex items-center px-3 py-1.5 rounded text-sm ${
          isActive 
            ? 'bg-blue-500 text-white' 
            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
        }`}
        onClick={() => handleSortChange(name)}
      >
        {label}
        {isActive && (
          sortDirection === 'asc' 
            ? <ArrowUpIcon className="h-3 w-3 ml-1" />
            : <ArrowDownIcon className="h-3 w-3 ml-1" />
        )}
      </button>
    );
  };
  
  return (
    <div className="h-full">
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="bg-white border-b border-slate-200 p-4">
          <h2 className="text-xl font-semibold text-slate-800">Category Analytics</h2>
          <p className="text-sm text-slate-500">Analyze sentiment and performance across different categories</p>
        </div>
        
        {/* Main content area with responsive layout */}
        <div className="flex flex-1 overflow-hidden">
          {/* Categories list - adjusts width based on whether detail is shown */}
          <div className={`${selectedCategory ? 'w-2/3 lg:w-3/4' : 'w-full'} overflow-auto p-4 bg-slate-50 h-full transition-all duration-300`}>
            {/* Sorting options */}
            <div className="flex flex-wrap gap-2 mb-4 bg-white p-3 rounded-lg shadow-sm">
              <span className="text-sm text-slate-500 flex items-center mr-2">
                Sort by:
              </span>
              <div className="flex flex-wrap gap-2">
                {renderSortButton('mentions', 'Mentions')}
                {renderSortButton('positive', 'Positive')}
                {renderSortButton('negative', 'Negative')}
                {renderSortButton('growth', 'Growth')}
                {renderSortButton('urgency', 'Urgency')}
                {renderSortButton('impact', 'Impact')}
                {renderSortButton('alphabetical', 'A-Z')}
              </div>
            </div>
            
            {/* Categories grid */}
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <Spinner className="h-8 w-8 text-blue-500" />
                <span className="ml-3 text-slate-500">Loading categories...</span>
              </div>
            ) : sortedData.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {sortedData.map((category, index) => (
                  <CategoryCard 
                    key={index} 
                    category={category}
                    onClick={handleCategorySelect}
                    isActive={selectedCategory && selectedCategory.name === category.name}
                  />
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-64 text-slate-500">
                <p className="text-lg font-medium">No categories found</p>
                <p className="text-sm">Try changing filters or loading different data</p>
              </div>
            )}
          </div>
          
          {/* Right pane for category details */}
          {selectedCategory && (
            <div className="w-1/3 lg:w-1/4 bg-white border-l border-slate-200 h-full overflow-auto flex flex-col transition-all duration-300">
              <div className="p-4 border-b border-slate-200 flex justify-between items-center sticky top-0 bg-white z-10">
                <h2 className="font-semibold text-slate-800">
                  {selectedCategory.name ? selectedCategory.name.replace(/_/g, ' ').toUpperCase() : 'CATEGORY'}
                </h2>
                <button 
                  className="text-slate-500 hover:text-slate-700 p-1 rounded-full hover:bg-slate-100"
                  onClick={handleCloseDetail}
                  aria-label="Close details"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
              <div className="flex-1 overflow-auto p-4">
                <CategoryDetail 
                  category={selectedCategory} 
                  onClose={handleCloseDetail}
                  inSidebar={true}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CategoryAnalytics; 